import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFacility } from "@/context/FacilityContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Check, ShieldCheck, Lock, Loader2, Trash2, Pencil, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const RESOURCES = ["dashboard", "facility", "department", "service", "user", "role", "module", "audit", "settings", "patient", "opd", "consultation", "opd_billing", "bed", "ipd", "finance"];
const ACTIONS = ["view", "create", "edit", "delete", "approve", "export", "manage_settings"];
const ACTION_LABEL = { view: "View", create: "Create", edit: "Edit", delete: "Delete", approve: "Approve", export: "Export", manage_settings: "Settings" };
const NON_EDITABLE = ["organisation_owner", "organisation_administrator"];

export default function Roles() {
  const { auth } = useAuth();
  const { has } = useFacility();
  const orgId = auth?.organisation?.id;
  const [roles, setRoles] = useState(null);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", scope: "facility", permissions: [] });
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [editPerms, setEditPerms] = useState([]);

  const load = () => api.get(`/roles?organisation_id=${orgId}`).then((r) => {
    setRoles(r.data);
    setSelected((s) => { const key = s?.id || s?.code; return r.data.find((x) => (x.id || x.code) === key) || r.data[0]; });
  }).catch(() => setRoles([]));
  useEffect(() => { if (orgId) load(); }, [orgId]);

  const togglePerm = (perm) => setForm((f) => ({ ...f, permissions: f.permissions.includes(perm) ? f.permissions.filter((p) => p !== perm) : [...f.permissions, perm] }));
  const save = async () => {
    setSaving(true);
    try { await api.post("/roles", { ...form, organisation_id: orgId }); toast.success("Custom role created"); setOpen(false); setForm({ name: "", description: "", scope: "facility", permissions: [] }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (role) => { try { await api.delete(`/roles/${role.id}`); toast.success("Role deleted"); setSelected(null); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const isEditable = (role) => role && !NON_EDITABLE.includes(role.code) && !(role.permissions || []).includes("*");
  const openEdit = (role) => {
    setEditRole(role);
    setEditPerms((role.permissions || []).filter((p) => p !== "*"));
    setEditOpen(true);
  };
  const toggleEditPerm = (perm) => setEditPerms((ps) => ps.includes(perm) ? ps.filter((p) => p !== perm) : [...ps, perm]);
  const saveEdit = async () => {
    setSaving(true);
    try {
      if (editRole.is_system) {
        await api.put(`/roles/${editRole.code}/overrides`, { organisation_id: orgId, permissions: editPerms });
      } else {
        await api.put(`/roles/${editRole.id}`, { name: editRole.name, description: editRole.description, scope: editRole.scope, permissions: editPerms, organisation_id: orgId });
      }
      toast.success("Permissions updated");
      setEditOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const resetOverride = async (role) => {
    try { await api.delete(`/roles/${role.code}/overrides?organisation_id=${orgId}`); toast.success("Reset to default permissions"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const hasPerm = (role, resource, action) => role?.permissions?.includes("*") || role?.permissions?.includes(`${resource}:${action}`);

  return (
    <div data-testid="roles-page">
      <PageHeader title="Roles & Permissions" subtitle="Role-based access control. Edit the permissions of any predefined role for your organisation, or create custom roles."
        actions={has("role:create") && <Button onClick={() => setOpen(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="create-role-button"><Plus className="h-4 w-4 mr-1.5" /> Custom Role</Button>} />

      {roles === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden self-start">
          <div className="px-4 py-3 border-b border-slate-200 text-[11px] font-mono uppercase tracking-wider text-slate-400">Roles ({roles.length})</div>
          <div className="max-h-[560px] overflow-y-auto thin-scroll divide-y divide-slate-100">
            {roles.map((r) => (
              <button key={r.id || r.code} onClick={() => setSelected(r)} data-testid={`role-item-${r.id || r.code}`}
                className={`w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 ${(selected?.id || selected?.code) === (r.id || r.code) ? "bg-blue-50" : ""}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-1.5">{r.is_system && <Lock className="h-3 w-3 text-slate-400" />}{r.name}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{r.scope}{r.has_override && <span className="ml-1.5 text-blue-500">· customised</span>}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          {selected && (
            <>
              <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
                <div>
                  <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-600" />{selected.name}
                    {selected.has_override && <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">Customised</span>}
                  </h3>
                  <p className="text-sm text-slate-500 mt-0.5">{selected.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {has("role:edit") && isEditable(selected) && <Button variant="outline" size="sm" onClick={() => openEdit(selected)} data-testid="edit-permissions-button"><Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit Permissions</Button>}
                  {selected.is_system && selected.has_override && has("role:edit") && <Button variant="outline" size="sm" onClick={() => resetOverride(selected)} data-testid="reset-role-button"><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset</Button>}
                  {!selected.is_system && has("role:delete") && <Button variant="outline" size="sm" onClick={() => del(selected)} className="text-red-600" data-testid="delete-role-button"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete</Button>}
                </div>
              </div>
              <div className="overflow-x-auto thin-scroll">
                <table className="w-full text-sm" data-testid="rbac-matrix">
                  <thead><tr className="bg-slate-50 text-left">
                    <th className="px-4 py-2.5 text-[11px] font-mono uppercase tracking-wider text-slate-400">Resource</th>
                    {ACTIONS.map((a) => <th key={a} className="px-2 py-2.5 text-center text-[11px] font-mono uppercase tracking-wider text-slate-400">{ACTION_LABEL[a]}</th>)}
                  </tr></thead>
                  <tbody>
                    {RESOURCES.map((res) => (
                      <tr key={res} className="border-t border-slate-100">
                        <td className="px-4 py-2.5 font-medium text-slate-700 capitalize">{res.replace("_", " ")}</td>
                        {ACTIONS.map((a) => (
                          <td key={a} className="px-2 py-2.5 text-center" data-testid={`rbac-${res}-${a}`}>
                            {hasPerm(selected, res, a) ? <Check className="h-4 w-4 text-emerald-600 mx-auto" /> : <span className="text-slate-200">·</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>)}

      {/* Create custom role */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto thin-scroll" data-testid="role-dialog">
          <DialogHeader><DialogTitle>New Custom Role</DialogTitle><DialogDescription>Define a new role and choose the permissions it grants in your organisation.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Role name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-name-input" /></div>
              <div className="space-y-1.5"><Label>Scope</Label>
                <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} data-testid="role-scope-select">
                  <option value="facility">Facility</option><option value="organisation">Organisation</option>
                </select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <PermissionMatrix selected={form.permissions} onToggle={togglePerm} testPrefix="role-perm" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving || !form.name} className="bg-blue-700 hover:bg-blue-800" data-testid="role-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create role"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit permissions (system override or custom role) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto thin-scroll" data-testid="edit-role-dialog">
          <DialogHeader><DialogTitle>Edit Permissions — {editRole?.name}</DialogTitle><DialogDescription>Tick the permissions this role should have in your organisation.</DialogDescription></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">{editRole?.is_system ? "This customises a predefined role; you can reset it to default later." : "Update this custom role's permissions."}</p>
          <PermissionMatrix selected={editPerms} onToggle={toggleEditPerm} testPrefix="edit-perm" />
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button><Button onClick={saveEdit} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="edit-role-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save permissions"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PermissionMatrix({ selected, onToggle, testPrefix }) {
  return (
    <div>
      <Label>Permissions</Label>
      <div className="mt-2 border border-slate-200 rounded-md overflow-x-auto thin-scroll">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50"><th className="px-3 py-2 text-left text-[11px] font-mono uppercase text-slate-400">Resource</th>{ACTIONS.map((a) => <th key={a} className="px-1 py-2 text-center text-[10px] font-mono uppercase text-slate-400">{ACTION_LABEL[a]}</th>)}</tr></thead>
          <tbody>
            {RESOURCES.map((res) => (
              <tr key={res} className="border-t border-slate-100">
                <td className="px-3 py-1.5 capitalize text-slate-700">{res.replace("_", " ")}</td>
                {ACTIONS.map((a) => (
                  <td key={a} className="px-1 py-1.5 text-center" data-testid={`${testPrefix}-${res}-${a}`}>
                    <Checkbox checked={selected.includes(`${res}:${a}`)} onCheckedChange={() => onToggle(`${res}:${a}`)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
